<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { IResponseData } from '~/types'
import { type TAboutProductHeader, WidgetAboutPageHeader } from '~/components/widget/about-page'
import { ContentBlock } from '~/components/ui/content-block'
import type { IInsurance } from '~/components/entity/insurance'
import { LucideCheck } from 'lucide-vue-next'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'
import { WidgetBranchesList } from '~/components/widget/branches'
import { WidgetInsuranceCalculators } from '~/components/widget/insurance-calculators'
import { WidgetInsuranceProductsList } from '~/components/widget/insurance-products'
import { WidgetRating } from '~/components/widget/rating'
import { WidgetSidebarNews } from '~/components/widget/sidebar-news'

const route = useRoute()
const { t, locale } = useI18n()
const { getPathWithLocale } = useLocalizedPath()
const insuranceUrl = `/insurance/${route.params.slug}`
const { data } = await useApiWith404<IResponseData<IInsurance>>(() => insuranceUrl, {
  key: `insurance-${route.params.slug}`,
  watch: [() => route.params.slug],
  immediate: true
})

const insurance = computed(() => data.value?.data)
const config = useRuntimeConfig()
const baseUrl = config.public.siteUrl
useSeoFromEntity(insurance, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.insurance_companies'), url: '/insurances' },
  { title: insurance.value?.title || t('common.insurance_companies'), url: insurance.value?.url }
])

const fitInsurance = computed<TAboutProductHeader>(() => ({
  title: insurance.value?.title ?? '',
  organization: {
    official_title: insurance.value?.official_title ?? '',
    url: '',
    status: '',
    status_comments: ''
  },
  views_count: insurance.value?.views_count ?? 0,
  updated_at: insurance.value?.updated_at ?? '',
  logo: insurance.value?.logo ?? '',
  logo_rectangle: insurance.value?.logo_rectangle ?? '',
  id: insurance.value?.id ?? 0,
  slug: insurance.value?.slug,
  status: insurance.value?.status,
  status_comments: insurance.value?.status_comments,
  url: insurance.value?.url ?? '',
  description: insurance.value?.description ?? '',
  call_center_phone: '',
  amount_from: ''
}))

const insuranceForWhom = computed(() => {
  const result: string[] = []
  const forWhom = insurance.value?.for_whom
  if (forWhom) {
    for (const key in forWhom) {
      if (forWhom[key]) result.push(key)
    }
  }
  return result
})

const hasInsuranceProducts = ref<boolean | null>(null)
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts
              v-if="insurance"
              :organization="insurance"
              :type="'insurance'"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSidebarNews
              v-if="insurance.news_count"
              :heading="t('page.organization.related_news_heading')"
              path="/news"
              :query-params="{
                per_page: '5',
                'filter[organization_type]': insurance.organization_type,
                'filter[organization_id]': insurance.id
              }"
              variant="white"
            />
          </aside>
        </template>
        <div>
          <div class="space-y-5" role="main">
            <WidgetAboutPageHeader v-if="insurance" type="insurance" :product="fitInsurance">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-2">
                <div v-if="insurance && insurance.website_url">
                  <div class="SecondaryTextHeadings">{{ $t('common.website') }}</div>
                  <a
                    :href="insurance.website_url"
                    class="text-sm text-blue-600 hover:underline"
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $t('common.go_to_website') }}
                  </a>
                </div>
                <div v-if="insurance && insurance.email">
                  <div class="SecondaryTextHeadings">{{ $t('common.email') }}</div>
                  <a
                    :href="`mailto:${insurance.email}`"
                    class="text-sm font-semibold text-blue-600 hover:underline"
                  >
                    {{ insurance.email }}
                  </a>
                </div>
              </div>

              <Content>
                <div v-hydrated-html="insurance?.description" />
              </Content>

              <Heading tag="h2" class="h6">{{
                $t('page.insurance.slug.for_whom_heading')
              }}</Heading>
              <div class="grid grid-cols-1 gap-2 md:grid-cols-3">
                <div v-for="item in insuranceForWhom" :key="item" class="flex gap-2">
                  <LucideCheck class="min-w-5 text-blue-600" />
                  <p>{{ $t(`page.insurance.slug.for_whom_options.${item}`) }}</p>
                </div>
              </div>
            </WidgetAboutPageHeader>

            <section v-if="insurance && insurance.types.length">
              <ContentBlock
                variant="default"
                :heading-text="$t('page.insurance.slug.insurance_types_heading')"
                heading-tag="h2"
                heading-class="h4"
              >
                <div class="grid grid-cols-1 gap-5 md:grid-cols-3">
                  <NuxtLink
                    v-for="item in insurance.types"
                    :key="item.slug"
                    :to="getPathWithLocale(item.url)"
                    class="flex items-center gap-2 rounded-md bg-white p-4 hover:bg-blue-100"
                  >
                    <div class="rounded-md bg-gray-100 p-2">
                      <div><UiIcon :name="item.slug" /></div>
                    </div>
                    <div>
                      <div class="text-sm font-semibold">
                        {{ item.title }}
                      </div>
                    </div>
                  </NuxtLink>
                </div>
              </ContentBlock>
            </section>

            <section v-if="insurance" v-show="hasInsuranceProducts">
              <ContentBlock
                variant="default"
                :heading-text="$t('page.insurance.slug.insurance_products_heading')"
                heading-tag="h2"
                heading-class="h4"
              >
                <WidgetInsuranceProductsList
                  :insurance-company-id="insurance.id"
                  @loaded="hasInsuranceProducts = $event"
                />
              </ContentBlock>
            </section>

            <section v-if="insurance && insurance.other_type_comment">
              <ContentBlock
                variant="white"
                :heading-text="$t('page.insurance.slug.other_insurance_types_heading')"
              >
                <Content>
                  <div v-hydrated-html="insurance?.other_type_comment" />
                </Content>
              </ContentBlock>
            </section>

            <WidgetInsuranceCalculators v-if="insurance.slug === 'bakai-insurance'" />

            <WidgetBranchesList
              v-if="insurance"
              :organization="insurance"
              :hide-organization-details="true"
              :hide-pagination="false"
            />

            <WidgetRating
              v-if="insurance && config.public.widgetRatingEnabled"
              :entity="insurance"
              entity-type="insurance_company"
              :heading-text="$t('page.insurance.slug.rating_heading', { title: insurance.title })"
            />
          </div>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
